"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { sendSmsCode, getSlideCaptcha, ApiError } from "@/lib/api";
import type { SlideCaptchaData } from "@/lib/api";

export type OtpPurpose = "login" | "register" | "bind";

export interface OtpState {
  codeDigits: string[];
  digitRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  smsSent: boolean;
  smsCooldown: number;
  smsLoading: boolean;
  /** 当前待发送的手机号和用途（滑块验证弹窗用） */
  pendingPhone: string;
  pendingPurpose: OtpPurpose;
  /** 滑块验证码数据，非 null 时展示滑块弹窗 */
  slideCaptcha: SlideCaptchaData | null;
  slideCaptchaLoading: boolean;
  slideCaptchaStatus: "idle" | "success" | "error";
  handleDigitChange: (
    index: number,
    value: string,
    onComplete: (code: string) => void,
  ) => void;
  handleDigitKeyDown: (index: number, e: React.KeyboardEvent) => void;
  handleDigitPaste: (
    e: React.ClipboardEvent,
    onComplete: (code: string) => void,
  ) => void;
  handleSendSms: (
    phone: string,
    purpose: OtpPurpose,
  ) => Promise<void>;
  handlePhoneNext: (
    phone: string,
    purpose: OtpPurpose,
    onSuccess: () => void,
  ) => Promise<void>;
  /** 滑块验证通过后调用，实际发送短信 */
  handleSlideVerify: (pointX: number, pointY: number) => Promise<void>;
  /** 刷新滑块验证码 */
  refreshSlideCaptcha: () => Promise<void>;
  /** 取消滑块验证 */
  cancelSlideCaptcha: () => void;
  resetDigits: () => void;
}

export interface AuthFormState {
  loading: boolean;
  setLoading: (v: boolean) => void;
  error: string;
  setError: (v: string) => void;
  shake: boolean;
  triggerShake: () => void;
}

export function useAuthForm(): { otp: OtpState; form: AuthFormState } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const [smsSent, setSmsSent] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 滑块验证相关状态
  const [slideCaptcha, setSlideCaptcha] = useState<SlideCaptchaData | null>(
    null,
  );
  const [slideCaptchaLoading, setSlideCaptchaLoading] = useState(false);
  const [slideCaptchaStatus, setSlideCaptchaStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  // 待发送的参数（等滑块验证完成后使用）
  const pendingRef = useRef<{
    phone: string;
    purpose: OtpPurpose;
    onSuccess: () => void;
  } | null>(null);
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingPurpose, setPendingPurpose] = useState<OtpPurpose>("login");

  // SMS cooldown timer
  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = setInterval(() => setSmsCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [smsCooldown]);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  }, []);

  const resetDigits = useCallback(() => {
    setCodeDigits(["", "", "", "", "", ""]);
  }, []);

  // 加载滑块验证码
  const loadSlideCaptcha = useCallback(async () => {
    setSlideCaptchaLoading(true);
    setSlideCaptchaStatus("idle");
    try {
      const data = await getSlideCaptcha();
      setSlideCaptcha(data);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.statusText || "获取验证码失败，请重试"
          : "获取验证码失败，请重试";
      setError(msg);
    } finally {
      setSlideCaptchaLoading(false);
    }
  }, []);

  const refreshSlideCaptcha = useCallback(async () => {
    await loadSlideCaptcha();
  }, [loadSlideCaptcha]);

  const cancelSlideCaptcha = useCallback(() => {
    setSlideCaptcha(null);
    setSlideCaptchaStatus("idle");
    pendingRef.current = null;
    setSmsLoading(false);
  }, []);

  // 滑块验证通过后实际发送短信
  const handleSlideVerify = useCallback(
    async (pointX: number, pointY: number) => {
      const pending = pendingRef.current;
      if (!pending || !slideCaptcha) return;
      setSlideCaptchaStatus("idle");
      setSmsLoading(true);
      setError("");
      try {
        await sendSmsCode(pending.phone, pending.purpose, {
          captchaId: slideCaptcha.captchaId,
          pointX,
          pointY,
        });
        setSlideCaptchaStatus("success");
        setSmsSent(true);
        setSmsCooldown(60);
        setCodeDigits(["", "", "", "", "", ""]);
        setTimeout(() => {
          setSlideCaptcha(null);
          setSlideCaptchaStatus("idle");
          pending.onSuccess();
          setTimeout(() => digitRefs.current[0]?.focus(), 100);
        }, 400);
      } catch (err) {
        if (err instanceof ApiError) {
          const needCaptcha = (
            err.body as { needCaptcha?: boolean } | null | undefined
          )?.needCaptcha;
          if (needCaptcha) {
            // 滑块坐标校验失败，刷新验证码让用户重试
            setSlideCaptchaStatus("error");
            setTimeout(() => {
              void loadSlideCaptcha();
            }, 800);
          } else {
            // 其他业务错误（如「验证失败次数过多，请明天再试」），
            // 关闭滑块弹窗，把后端返回的 msg 显示在表单上
            setSlideCaptcha(null);
            setSlideCaptchaStatus("idle");
            pendingRef.current = null;
            setError(err.statusText || "发送验证码失败，请稍后重试");
          }
        } else {
          // 网络或未知错误，默认刷新验证码重试
          setSlideCaptchaStatus("error");
          setTimeout(() => {
            void loadSlideCaptcha();
          }, 800);
        }
      } finally {
        setSmsLoading(false);
      }
    },
    [slideCaptcha, loadSlideCaptcha],
  );

  // 重发短信（需要重新过滑块）
  const handleSendSms = useCallback(
    async (phone: string, purpose: OtpPurpose) => {
      if (!phone.trim() || smsCooldown > 0 || smsLoading) return;
      setPendingPhone(phone.trim());
      setPendingPurpose(purpose);
      pendingRef.current = {
        phone: phone.trim(),
        purpose,
        onSuccess: () => {
          /* 重发不跳步骤 */
        },
      };
      await loadSlideCaptcha();
    },
    [smsCooldown, smsLoading, loadSlideCaptcha],
  );

  const handlePhoneNext = useCallback(
    async (
      phone: string,
      purpose: OtpPurpose,
      onSuccess: () => void,
    ) => {
      const trimmed = phone.trim();
      if (!trimmed) {
        setError("请输入手机号");
        return;
      }
      if (!/^1\d{10}$/.test(trimmed)) {
        setError("请输入正确的手机号");
        return;
      }
      setError("");
      setPendingPhone(trimmed);
      setPendingPurpose(purpose);
      pendingRef.current = { phone: trimmed, purpose, onSuccess };
      await loadSlideCaptcha();
    },
    [loadSlideCaptcha],
  );

  const handleDigitChange = useCallback(
    (index: number, value: string, onComplete: (code: string) => void) => {
      const digit = value.replace(/\D/g, "").slice(-1);
      setCodeDigits((prev) => {
        const next = [...prev];
        next[index] = digit;
        const fullCode = next.join("");
        if (fullCode.length === 6 && next.every(Boolean)) {
          onComplete(fullCode);
        }
        return next;
      });
      setError("");
      if (digit && index < 5) {
        digitRefs.current[index + 1]?.focus();
      }
    },
    [],
  );

  const handleDigitKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace") {
        setCodeDigits((prev) => {
          if (!prev[index] && index > 0) {
            digitRefs.current[index - 1]?.focus();
          }
          return prev;
        });
      }
    },
    [],
  );

  const handleDigitPaste = useCallback(
    (e: React.ClipboardEvent, onComplete: (code: string) => void) => {
      e.preventDefault();
      const paste = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);
      if (paste.length > 0) {
        const next = ["", "", "", "", "", ""];
        for (let i = 0; i < paste.length; i++) next[i] = paste[i];
        setCodeDigits(next);
        const focusIdx = Math.min(paste.length, 5);
        digitRefs.current[focusIdx]?.focus();
        if (paste.length === 6) {
          onComplete(paste);
        }
      }
    },
    [],
  );

  return {
    otp: {
      codeDigits,
      digitRefs,
      smsSent,
      smsCooldown,
      smsLoading,
      pendingPhone,
      pendingPurpose,
      slideCaptcha,
      slideCaptchaLoading,
      slideCaptchaStatus,
      handleDigitChange,
      handleDigitKeyDown,
      handleDigitPaste,
      handleSendSms,
      handlePhoneNext,
      handleSlideVerify,
      refreshSlideCaptcha,
      cancelSlideCaptcha,
      resetDigits,
    },
    form: {
      loading,
      setLoading,
      error,
      setError,
      shake,
      triggerShake,
    },
  };
}
